import type { ReactNode } from "react";

/**
 * Campo editabile riusabile per Rinascente / BSC / DDT detail.
 *
 * Mostra il valore effettivo (correction ?? extracted). Se il valore differisce
 * dall'estratto, evidenzia il campo (badge "modificato" + sfondo tenue) e mostra
 * un'icona "undo" per annullare la singola modifica e tornare al valore estratto.
 *
 * In sola lettura (pratica conclusa o lock di altro operatore) mostra il valore
 * come testo, senza input.
 */
interface EditableFieldProps {
  label: string;
  /** Valore effettivo (correction ?? extracted). */
  value: string | number | null | undefined;
  /** Valore estratto originale (per highlight "modificato" + undo). */
  originalValue?: string | number | null | undefined;
  onChange: (v: string) => void;
  /** Annulla la singola modifica — ripristina il valore estratto. */
  onReset?: () => void;
  readOnly?: boolean;
  type?: "text" | "number";
  placeholder?: string;
  /** Badge/icona opzionale affianco alla label (es. match method). */
  badge?: ReactNode;
  multiline?: boolean;
}

function normalize(v: unknown): string {
  if (v === null || v === undefined) return "";
  return String(v);
}

export function EditableField({
  label,
  value,
  originalValue,
  onChange,
  onReset,
  readOnly = false,
  type = "text",
  placeholder,
  badge,
  multiline = false,
}: EditableFieldProps) {
  const isCorrected = normalize(value) !== normalize(originalValue);
  const display = normalize(value);

  return (
    <div
      className="form-group"
      style={{
        marginBottom: "var(--space-2)",
        position: "relative",
        padding: isCorrected && !readOnly ? "var(--space-2)" : undefined,
        margin: isCorrected && !readOnly ? "calc(-1 * var(--space-2))" : undefined,
        background: isCorrected && !readOnly ? "var(--indaco-warning-light)" : undefined,
        borderRadius: "var(--border-radius-sm)",
        border: isCorrected && !readOnly ? "1px solid var(--indaco-warning)" : "1px solid transparent",
      }}
    >
      <label
        className="form-label"
        style={{ display: "flex", alignItems: "center", gap: "var(--space-2)" }}
      >
        {label}
        {badge}
        {isCorrected && !readOnly && (
          <span
            className="badge badge-warning"
            style={{ fontSize: "var(--font-size-xs)", padding: "1px 6px", fontWeight: "var(--font-weight-semibold)" }}
            title="Valore modificato rispetto all'estrazione"
          >
            modificato
          </span>
        )}
      </label>
      {readOnly ? (
        <div
          className="text-sm"
          style={{
            padding: "var(--space-2) 0",
            color: display === "" ? "var(--indaco-gray-400)" : "inherit",
            fontStyle: display === "" ? "italic" : "normal",
            wordBreak: "break-word",
          }}
        >
          {display === "" ? (placeholder ?? "—") : display}
        </div>
      ) : (
        <div style={{ display: "flex", gap: "var(--space-2)", alignItems: "flex-start" }}>
          {multiline ? (
            <textarea
              className="form-textarea"
              style={{ flex: 1, fontSize: "var(--font-size-sm)" }}
              rows={2}
              value={display}
              placeholder={placeholder ?? "(vuoto)"}
              onChange={(e) => onChange(e.target.value)}
            />
          ) : (
            <input
              type={type}
              className="form-input"
              style={{ flex: 1, fontSize: "var(--font-size-sm)" }}
              value={display}
              placeholder={placeholder ?? "(vuoto)"}
              onChange={(e) => onChange(e.target.value)}
            />
          )}
          {isCorrected && onReset && (
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              title="Annulla modifica — ripristina il valore estratto"
              onClick={onReset}
              style={{ padding: "var(--space-1)", minWidth: 32 }}
            >
              <span className="material-symbols-outlined" style={{ fontSize: 18 }}>undo</span>
            </button>
          )}
        </div>
      )}
    </div>
  );
}
